<template>
    <BaseModal
        :title="isEditing ? 'Edit Student' : 'Add Student'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="4xl"
        @close="emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Personal Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Personal Information
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <!-- Name -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Name *
                        </label>
                        <input
                            v-model="form.name"
                            type="text"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.name,
                            }"
                        />
                        <p v-if="errors.name" class="mt-1 text-sm text-red-600">
                            {{ errors.name }}
                        </p>
                    </div>
                    <!-- Email -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Email *
                        </label>
                        <input
                            v-model="form.email"
                            type="email"
                            required
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.email,
                            }"
                        />
                        <p
                            v-if="errors.email"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.email }}
                        </p>
                    </div>
                    <!-- Birth Date -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Birth Date
                        </label>
                        <input
                            v-model="form.birth_date"
                            type="date"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.birth_date,
                            }"
                        />
                        <p
                            v-if="errors.birth_date"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.birth_date }}
                        </p>
                    </div>
                    <!-- Gender -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Gender
                        </label>
                        <div class="flex gap-6">
                            <div class="flex items-center">
                                <input
                                    v-model="form.gender"
                                    type="radio"
                                    id="male"
                                    value="male"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <label
                                    for="male"
                                    class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                >
                                    Male
                                </label>
                            </div>
                            <div class="flex items-center">
                                <input
                                    v-model="form.gender"
                                    type="radio"
                                    id="female"
                                    value="female"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <label
                                    for="female"
                                    class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                >
                                    Female
                                </label>
                            </div>
                        </div>
                        <p
                            v-if="errors.gender"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.gender }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Academic Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Academic Information
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <!-- NISN -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            NISN
                        </label>
                        <input
                            v-model="form.nisn"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.nisn,
                            }"
                        />
                        <p v-if="errors.nisn" class="mt-1 text-sm text-red-600">
                            {{ errors.nisn }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Contact Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Contact Information
                </h4>
                <div>
                    <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                        Address
                    </label>
                    <textarea
                        v-model="form.address"
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        :class="{
                            'border-red-500': errors.address,
                        }"
                    ></textarea>
                    <p v-if="errors.address" class="mt-1 text-sm text-red-600">
                        {{ errors.address }}
                    </p>
                </div>
                <div class="grid grid-cols-2 gap-4 mt-4">
                    <!-- Phone -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Phone
                        </label>
                        <input
                            v-model="form.phone"
                            type="tel"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.phone,
                            }"
                        />
                        <p
                            v-if="errors.phone"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.phone }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Emergency Contact Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Emergency Contact
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <!-- Emergency Contact Name -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Emergency Contact Name
                        </label>
                        <input
                            v-model="form.emergency_contact"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.emergency_contact,
                            }"
                        />
                        <p
                            v-if="errors.emergency_contact"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.emergency_contact }}
                        </p>
                    </div>
                    <!-- Emergency Phone -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Emergency Phone
                        </label>
                        <input
                            v-model="form.emergency_phone"
                            type="tel"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.emergency_phone,
                            }"
                        />
                        <p
                            v-if="errors.emergency_phone"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.emergency_phone }}
                        </p>
                    </div>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import {
    studentApi,
    type Student,
    type CreateStudentData,
    type UpdateStudentData,
} from "@/api/student";
import BaseModal from "@/components/BaseModal.vue";

interface Props {
    student?: Student | null;
    isEditing: boolean;
    showModal: boolean;
}

interface Emits {
    (e: "close"): void;
    (e: "save"): void;
    (e: "saved"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// State
const loading = ref(false);
const errors = ref<Record<string, string>>({});

// Form data
const form = ref<CreateStudentData>({
    name: "",
    email: "",
    nisn: "",
    birth_date: "",
    gender: "",
    phone: "",
    address: "",
    emergency_contact: "",
    emergency_phone: "",
});

// Reset form - MUST be defined BEFORE watchers that reference it
const resetForm = () => {
    form.value = {
        name: "",
        email: "",
        nisn: "",
        birth_date: "",
        gender: "",
        phone: "",
        address: "",
        emergency_contact: "",
        emergency_phone: "",
    };
    errors.value = {};
};

// Initialize form when editing
watch(
    () => props.student,
    (newStudent) => {
        if (newStudent) {
            form.value = {
                name: newStudent.user?.name || "",
                email: newStudent.user?.email || "",
                nisn: newStudent.nisn || "",
                birth_date: newStudent.birth_date?.split("T")[0] ?? "",
                gender: newStudent.gender || "",
                phone: newStudent.phone || "",
                address: newStudent.address || "",
                emergency_contact: newStudent.emergency_contact || "",
                emergency_phone: newStudent.emergency_phone || "",
            };
        } else {
            resetForm();
        }
    },
    { immediate: true },
);

// Watch for modal opening
watch(
    () => props.showModal,
    (isOpen: boolean) => {
        console.log("Modal visibility changed:", isOpen);
    },
    { immediate: true },
);

// Validate form
const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.value.name || !form.value.name.trim()) {
        newErrors.name = "Name is required";
    }

    if (!form.value.email || !form.value.email.trim()) {
        newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        newErrors.email = "Email is invalid";
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

// Handle form submission
const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    errors.value = {};

    try {
        if (props.isEditing && props.student) {
            const updateData: UpdateStudentData = {
                name: form.value.name,
                email: form.value.email,
                nisn: form.value.nisn,
                birth_date: form.value.birth_date,
                gender: form.value.gender,
                phone: form.value.phone,
                address: form.value.address,
                emergency_contact: form.value.emergency_contact,
                emergency_phone: form.value.emergency_phone,
            };

            await studentApi.update(props.student.id, updateData);
            resetForm();
            emit("saved");
        } else {
            await studentApi.create(form.value);
            resetForm();
            emit("saved");
        }
        emit("close");
    } catch (error: any) {
        if (error.response?.data?.errors) {
            const apiErrors = error.response.data.errors;
            for (const [key, messages] of Object.entries(apiErrors)) {
                (errors.value as any)[key] = Array.isArray(messages)
                    ? (messages as string[])[0]
                    : String(messages);
            }
        } else {
            console.error("Form submission error:", error);
        }
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
/* Form transitions */
input,
select,
textarea {
    transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
}

input:focus,
select:focus,
textarea:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button transitions */
button {
    transition: all 0.2s ease-in-out;
}

/* Error states */
.border-red-500 {
    border-color: #ef4444;
}

.text-red-600 {
    color: #dc2626;
}

/* Dark mode adjustments */
.dark input,
.dark select,
.dark textarea {
    background-color: #1f2937;
    border-color: #4b5563;
    color: #f9fafb;
}

.dark input:focus,
.dark select:focus,
.dark textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>
